import IconArrowLeft from 'assets/icons/arrow-left.svg';
import Container from 'components/container';
import FunnelCta from 'components/funnel/cta';
import MinimumBasketValueNotice from 'components/minimum-basket-value-notice';
import PawLoader from 'components/paw-loader';
import Price from 'components/price';
import Saving from 'components/saving';
import { useAuth } from 'contexts/auth';
import { useCart } from 'contexts/cart';
import useMOVConditions from 'hooks/common/use-mov-conditions';
import { PdProductItem } from 'interfaces/line-item';
import { useSubscriptionDetailsState } from 'modules/account/pages/subscription/state';
import { Gtm } from 'tracking';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { retrieveValueFromUrlQuery } from 'utils/query-helper';
import { showErrorToast } from 'utils/toasts';
import CartItems from '../cart-items';
import { FUNNEL_STEPS } from '../constants';
import { useSubscriptionFunnel } from '../state';
import SubsDeliveyInfo from './subs-delivery-info';

const StepOverview: React.FC = () => {
  const router = useRouter();

  const {
    goToStep,
    petType,
    setLoading,
    subscriptionId,
    selectedCategories,
    isSingleProductPurchase,
    isExistingSubscription,
  } = useSubscriptionFunnel();

  const singleProductDiscountCode = router.query.couponCode?.toString();

  const {
    items,
    effectiveCart,
    goToCheckout,
    cartTotal,
    addToCart,
    isCartReady,
    resetCheckout,
    setCouponCode,
  } = useCart();

  const { loading } = useAuth();

  const [oneOffProductDetails, setOneOffProductDetails] = React.useState<PdProductItem | null>(
    null
  );

  const { subscription, updateSubscription, addOneOffItem } = useSubscriptionDetailsState({
    id: subscriptionId,
  });

  const addToCartRef = React.useRef(false);

  const productVariantId = useMemo(
    () =>
      retrieveValueFromUrlQuery({
        query: router.query,
        paramKey: 'variantId',
      }),
    [router.query]
  );

  const quantity = useMemo(
    () =>
      retrieveValueFromUrlQuery({
        query: router.query,
        paramKey: 'quantity',
      }),
    [router.query]
  );

  /**
   * Execute cart in operation as a single purchase funnel.
   * or when isExistingSubscription is true i.e subscription-one-time-purchase
   * so that we can keep the data in cart.
   * User lands on url from marketing email.
   */
  React.useEffect(() => {
    if (productVariantId && isSingleProductPurchase && addToCartRef.current === false) {
      // @ TODO : we should remove the checkout when user drop out of the funnel
      addToCart([
        {
          variantId: String(productVariantId),
          quantity: Number(quantity) ?? 1,
        },
      ]);

      addToCartRef.current = true;
    }
  }, [productVariantId, quantity, isSingleProductPurchase, addToCart]);

  React.useEffect(() => {
    if (singleProductDiscountCode) {
      setCouponCode(singleProductDiscountCode);
    }
  }, [setCouponCode, singleProductDiscountCode]);

  React.useEffect(() => {
    const oneOffProductDetails = items.find((item) => item.variantId === Number(productVariantId));
    if (oneOffProductDetails) setOneOffProductDetails(oneOffProductDetails);
  }, [items, productVariantId]);

  const updatedSubscriptionItems = React.useMemo(() => {
    let updatedItems;

    if (subscription) {
      const updatedSubsItems = subscription.items.map((subItem) => {
        const matchedItem = items.find((item) => subItem.variantId === item.variantId);

        if (matchedItem) {
          // increase the quantity if item already exist in subscription
          return {
            ...matchedItem,
            quantity: subItem.quantity + matchedItem.quantity,
          };
        }

        return subItem;
      });

      // for new added items that doesnt exist in existing subscription
      const filteredItems = items.filter((cartItem) => {
        return !updatedSubsItems.some((subItem) => subItem.variantId === cartItem.variantId);
      });

      updatedItems = {
        id: subscription.id,
        shortId: subscription.shortId,
        itemsCount: subscription.itemsCount,
        periodEndAt: subscription.periodEndAt,
        intervalInDays: subscription.intervalInDays,
        items: [...filteredItems, ...updatedSubsItems],
        shipping: subscription.shipping,
        shippingAddressId: subscription.shippingAddressId,
      };
    }

    return updatedItems;
  }, [subscription, items]);

  const updateSubscriptionItems = async (): Promise<void> => {
    const trackingPromises = items.map((item) => {
      Gtm.track({
        group: Gtm.Group.Subscription,
        name: Gtm.SubscriptionEvent.AddItem,
        data: {
          item: { variantId: item.variantId },
        },
      });
    });

    if (isSingleProductPurchase && oneOffProductDetails) {
      try {
        goToStep(FUNNEL_STEPS.CONFIRMATION);
        await addOneOffItem({ id: subscriptionId, oneOffProductDetails });
      } catch (error) {
        console.error(error);
        showErrorToast({
          error: 'error:unknown',
          caller: 'StepOverview',
        });
      } finally {
        setLoading(false);
        // remove the checkout that is stored on subscription-one-time-purchase mode
        resetCheckout();
      }
      await Promise.all(trackingPromises);

      return;
    }

    try {
      goToStep(FUNNEL_STEPS.CONFIRMATION);
      await updateSubscription(updatedSubscriptionItems);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
    await Promise.all(trackingPromises);
  };

  const { insufficientMoney, shouldShowMinimumBasketNotice } = useMOVConditions({
    effectiveCart,
    isInCheckout: true,
  });

  if ((!isCartReady || loading) && isSingleProductPurchase) {
    return (
      <div className="flex h-105 w-full items-center justify-center">
        <PawLoader />
      </div>
    );
  }

  return (
    <div
      className="bg-other-white w-full lg:max-w-2xl lg:rounded-lg lg:shadow-lg"
      data-test="subscription-funnel-overview"
    >
      {shouldShowMinimumBasketNotice && !isExistingSubscription && (
        <div className="mx-4 mt-1 py-2 text-sm lg:hidden">
          <MinimumBasketValueNotice insufficientMoney={insufficientMoney} />
        </div>
      )}
      <Container>
        <h1 className="text-primary-main flex text-xl leading-6 font-medium lg:pt-4 lg:text-4xl lg:leading-10 lg:font-black">
          {!isSingleProductPurchase && (
            <button
              className="focus-default mr-4 pt-1 pb-2"
              onClick={() => {
                goToStep(FUNNEL_STEPS.PRODUCTS);
              }}
            >
              <IconArrowLeft className="h-6 w-6" />
            </button>
          )}
          <FormattedMessage
            id={`subscription-funnel:overview:header${
              isExistingSubscription ? '-existing-order' : ''
            }`}
          />
        </h1>

        <p className="text-text-light-bg-secondary mt-2 lg:mt-4 lg:text-lg">
          <FormattedMessage
            id={`subscription-funnel:overview:sub-header${
              isExistingSubscription ? '-existing-order' : ''
            }`}
          />
        </p>

        <Saving
          items={items}
          blueText
          data-test="subscription-funnel-overview-saving"
          renderWithBox
        />

        {isExistingSubscription && (
          <div className="text-primary-main mt-4 font-medium">
            <FormattedMessage id="subscription-funnel:overview:new-items-existing-order" />
          </div>
        )}

        <CartItems items={items} canChangeAmount />

        <div className="bg-brand-tint -mx-6 mt-2 flex justify-between rounded-b-lg px-6 py-3 sm:hidden">
          <div>
            <strong className="text-primary-main">
              <FormattedMessage id="cart:total" />
            </strong>
            <div className="text-sm">
              <FormattedMessage id="cart:tax" />
            </div>
          </div>
          <strong className="text-primary-main text-3xl">
            <Price cents value={cartTotal} />
          </strong>
        </div>

        {isExistingSubscription && <SubsDeliveyInfo />}

        <FunnelCta
          visible={
            (selectedCategories.length > 0 &&
              cartTotal > 0 &&
              petType !== null &&
              !shouldShowMinimumBasketNotice) ||
            isSingleProductPurchase ||
            isExistingSubscription
          }
          onClick={() => {
            isExistingSubscription ? updateSubscriptionItems() : goToCheckout();
          }}
          data-test="subscription-funnel-overview-button-to-checkout"
        >
          <FormattedMessage
            id={`subscription-funnel:overview:button:${
              isExistingSubscription ? `to-subscription-update` : `to-checkout`
            }`}
          />
        </FunnelCta>
      </Container>
    </div>
  );
};

export default StepOverview;
